import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SVGAssets } from '@business/assets/vectors';
import {
  useGetUserFoldersQuery,
  useAddMultipleAssetsToFolderMutation
} from '@ports/internal-services/folder/folderSlice';
import { MovePhotoModalProps } from '@business/interfaces/photo';
import { Button } from '@business/workflows/ComponentUI';
import {
  useAddRecipeToUserFolderMutation,
  useBulkAddRecipesToUserFolderMutation
} from '@ports/internal-services/recipe/recipeSlice';

const MovePhotoModal: React.FC<MovePhotoModalProps> = ({
  isOpen,
  onClose,
  onError,
  assetId,
  assetIds,
  onSuccess,
  module
}) => {
  const { t } = useTranslation();
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'folders' | 'moments'>('folders');

  const { data: foldersData, isLoading } = useGetUserFoldersQuery({
    module: module ? module : undefined
  });
  const [addAssetsToFolder, { isLoading: isMoving }] =
    useAddMultipleAssetsToFolderMutation();

  const [addRecipeToFolder] = useAddRecipeToUserFolderMutation();
  const [bulkAddRecipesToFolder] = useBulkAddRecipesToUserFolderMutation();

  // Determine the asset IDs to move
  const assetsToMove =
    assetIds && assetIds.length > 0 ? assetIds : assetId ? [assetId] : [];
  const isBulkMove = assetIds && assetIds.length > 1;

  const recipeIds =
    module === 'recipe' && assetId
      ? assetId.includes(',')
        ? assetId.split(',')
        : [assetId]
      : [];

  const isBulkRecipeMove = module === 'recipe' && recipeIds.length > 1;

  const handleMove = async () => {
    if (!selectedFolderId || assetsToMove.length === 0) return;

    try {
      if (module === 'recipe') {
        // Check if assetId contains multiple IDs (comma-separated)
        const recipeIds = assetId.includes(',')
          ? assetId.split(',')
          : [assetId];

        if (recipeIds.length > 1) {
          // Use bulk mutation for multiple recipes
          await bulkAddRecipesToFolder({
            recipeFolder: {
              recipeIds: recipeIds,
              userFolderId: selectedFolderId
            }
          }).unwrap();
        } else {
          // Use single mutation for one recipe
          await addRecipeToFolder({
            recipeFolder: {
              recipeId: assetId,
              userFolderId: selectedFolderId
            }
          }).unwrap();
        }
      } else {
        await addAssetsToFolder({
          id: selectedFolderId,
          body: {
            assetIds: assetsToMove
          }
        }).unwrap();
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      onError?.();
      console.error('Failed to move asset:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {module === 'recipe'
              ? isBulkRecipeMove
                ? t('recipesTab.moveRecipeCount', {
                    count: recipeIds?.length
                  })
                : t('recipesTab.moveRecipe')
              : isBulkMove
                ? t('photosPage.movePhoto', { count: assetsToMove.length })
                : t('photosPage.moveMedia')}
          </h2>
          <Button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            btnStyle="no-outline">
            <SVGAssets.CloseBtn />
          </Button>
        </div>

        {/* Icon */}
        <div className="flex justify-center py-6">
          <div className="w-20 h-20 text-[#7F9D48]">
            <SVGAssets.MoveTo />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-4">
          <Button
            onClick={() => setActiveTab('folders')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'folders'
                ? 'text-[#7F9D48] border-b-2 border-[#7F9D48]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            btnStyle="no-outline">
            {t('photosPage.folders')}
          </Button>
          {/* {module !== 'recipe' && (
            <Button
              onClick={() => setActiveTab('moments')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'moments'
                  ? 'text-[#7F9D48] border-b-2 border-[#7F9D48]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              btnStyle="no-outline">
              {t('photosPage.moments')}
            </Button>
          )} */}
        </div>

        {/* Folder List */}
        <div className="p-4 max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7F9D48]" />
            </div>
          ) : activeTab === 'folders' ? (
            <div className="space-y-2">
              {foldersData?.data?.rows?.map(folder => (
                <Button
                  key={folder.id}
                  onClick={() => setSelectedFolderId(folder.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                    selectedFolderId === folder.id
                      ? 'bg-[#7F9D48] bg-opacity-10 border-2 border-[#7F9D48]'
                      : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                  }`}
                  btnStyle="no-outline">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">
                      {folder.name}
                    </span>
                    {selectedFolderId === folder.id && <SVGAssets.TickIcons />}
                  </div>
                </Button>
              ))}
              {foldersData?.data?.rows?.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  {t('photosPage.noFolders')}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {t('photosPage.momentsComingSoon')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t">
          <Button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            btnStyle="no-outline">
            {t('cancel')}
          </Button>
          <Button
            onClick={handleMove}
            disabled={!selectedFolderId || isMoving}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedFolderId && !isMoving
                ? 'bg-[#7F9D48] text-white hover:bg-[#6B8339]'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            btnStyle="no-outline">
            {isMoving ? t('photosPage.moving') : t('photosPage.move')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MovePhotoModal;
